import { frontendURL } from '../../../../helper/URLHelper';
import { REDIS_FEATURE_FLAGS } from '../../../../featureFlags';

const SECTION_CONTENT_FLOWS = 'SECTION_CONTENT_FLOWS';
const SECTION_AGENTS = 'SECTION_AGENTS';
const SECTION_PUBLISH = 'SECTION_PUBLISH';

const PROJECT_INDEPENDENT = new Set(['claudia_create_project']);

const claudia = (
  accountId,
  { hasProjects = true, isHubAdmin = false } = {}
) => {
  const disabledFor = toStateName =>
    !hasProjects && !PROJECT_INDEPENDENT.has(toStateName);

  // Creating a project is a Hub-admin-only action (claudia enforces it on the
  // backend); hide the entry for everyone else instead of showing a screen they
  // can't use.
  //
  // There is no claudia-only exclusion left: the one entry that needed it (AI
  // Tags & Attributes) now lives in Settings > Data > AI, so the set backing it
  // was empty and the check could never hide anything.
  const isVisible = toStateName =>
    isHubAdmin || toStateName !== 'claudia_create_project';

  return {
    parentNav: 'claudia',
    routes: [
      'claudia_create_project',
      'eddie_view',
      'eddie_view_legacy',
      'claudia_controlled_flows',
      'claudia_controlled_flows_catchall',
      'knowledge_base_view',
      'knowledge_base_view_catchall',
      'claudia_context_enrichment',
      'claudia_context_enrichment_catchall',
      'improvement_center_view',
      'improvement_center_view_catchall',
      'claudia_tools',
      'claudia_tools_catchall',
      'claudia_agents',
      'claudia_mcp',
      'claudia_settings',
      'claudia_deploy',
    ],
    menuItems: [
      {
        icon: 'add-circle',
        label: 'CLAUDIA_CREATE_PROJECT',
        hasSubMenu: false,
        toState: frontendURL(`accounts/${accountId}/claudia/create-project`),
        toStateName: 'claudia_create_project',
        disabled: disabledFor('claudia_create_project'),
        emphasized: !hasProjects,
      },
      {
        icon: 'settings',
        label: 'CLAUDIA_SETTINGS',
        hasSubMenu: false,
        toState: frontendURL(`accounts/${accountId}/claudia/settings`),
        toStateName: 'claudia_settings',
        featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_SETTINGS,
        disabled: disabledFor('claudia_settings'),
      },
      {
        iconType: 'claudia',
        icon: 'file',
        label: 'CLAUDIA_CONTENT',
        hasSubMenu: false,
        section: SECTION_CONTENT_FLOWS,
        key: 'claudia_content',
        toState: frontendURL(`accounts/${accountId}/claudia/knowledge-bases`),
        toStateName: 'knowledge_base_view',
        disabled: disabledFor('knowledge_base_view'),
      },
      {
        icon: 'globe-line',
        iconLib: 'lucide',
        label: 'CLAUDIA_CONTEXT_ENRICHMENT',
        hasSubMenu: false,
        section: SECTION_CONTENT_FLOWS,
        key: 'claudia_context_enrichment',
        wrapLabel: true,
        toState: frontendURL(
          `accounts/${accountId}/claudia/context-enrichment`
        ),
        toStateName: 'claudia_context_enrichment',
        disabled: disabledFor('claudia_context_enrichment'),
      },
      {
        iconType: 'claudia',
        icon: 'coral',
        label: 'CLAUDIA_CONTROLLED_FLOWS_V2',
        hasSubMenu: false,
        section: SECTION_CONTENT_FLOWS,
        toState: frontendURL(`accounts/${accountId}/claudia/controlled-flows`),
        toStateName: 'claudia_controlled_flows',
        disabled: disabledFor('claudia_controlled_flows'),
      },
      {
        iconType: 'claudia',
        icon: 'sparkle',
        label: 'CLAUDIA_IMPROVEMENT_CENTER',
        hasSubMenu: false,
        section: SECTION_CONTENT_FLOWS,
        key: 'claudia_improvement_center',
        toState: frontendURL(
          `accounts/${accountId}/claudia/improvement-center`
        ),
        toStateName: 'improvement_center_view',
        newFeatureFlag: 'enable_improvement_center',
        disabled: disabledFor('improvement_center_view'),
      },
      {
        // Cloud icon to match claudia-app's Agentes tab in the MF switcher
        // (react-feather `Cloud`). Uses a dedicated `cloud-line` key (not the
        // shared `cloud` Fluent icon, which the dashboard reuses for the API
        // inbox type) so the stroke restyle stays isolated to this sidebar.
        // `iconLib: 'lucide'` selects the stroke renderer — same convention as
        // the MCP `server` item above.
        icon: 'cloud-line',
        iconLib: 'lucide',
        label: 'CLAUDIA_CONVERSATIONAL_AGENTS',
        hasSubMenu: false,
        section: SECTION_AGENTS,
        toState: frontendURL(`accounts/${accountId}/claudia/agents`),
        toStateName: 'claudia_agents',
        featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_AGENTS_V2,
        disabled: disabledFor('claudia_agents'),
      },
      {
        iconType: 'claudia',
        icon: 'repair-tools',
        label: 'CLAUDIA_TOOLS',
        hasSubMenu: false,
        section: SECTION_AGENTS,
        toState: frontendURL(`accounts/${accountId}/claudia/tools`),
        toStateName: 'claudia_tools',
        featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_AGENTS_V2,
        disabled: disabledFor('claudia_tools'),
      },
      {
        // Server (rack) icon to match claudia-app's MCP tab in the MF switcher
        // (react-feather `Server`, same glyph as lucide). `iconLib: 'lucide'`
        // selects the stroke renderer so it draws outlined like the switcher,
        // not filled — same convention as the other stroke icons in the app.
        icon: 'server',
        iconLib: 'lucide',
        label: 'CLAUDIA_MCP_SERVERS',
        hasSubMenu: false,
        section: SECTION_AGENTS,
        toState: frontendURL(`accounts/${accountId}/claudia/mcp`),
        toStateName: 'claudia_mcp',
        featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_AGENTS_V2,
        disabled: disabledFor('claudia_mcp'),
      },
      {
        iconType: 'claudia',
        icon: 'rocket',
        label: 'CLAUDIA_DEPLOY',
        hasSubMenu: false,
        section: SECTION_PUBLISH,
        toState: frontendURL(`accounts/${accountId}/claudia/deploy`),
        toStateName: 'claudia_deploy',
        featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_DEPLOY,
        disabled: disabledFor('claudia_deploy'),
      },
      {
        // Rendered as the bottom-pinned "CH Button" (orange surface) per the
        // Hub V2 sidebar Figma. Opens the Playground overlay instead of routing.
        iconType: 'fluent-v2',
        icon: 'flask-alt',
        label: 'CLAUDIA_PLAYGROUND',
        hasSubMenu: false,
        action: 'togglePlayground',
        pinnedToBottom: true,
        disabled: !hasProjects,
      },
    ].filter(item => isVisible(item.toStateName)),
  };
};

export default claudia;
